/**
 * AI streaming client — POST + reader.read() pattern.
 *
 * EventSource doesn't support POST and doesn't carry credentials nicely, so
 * we hand-roll SSE parsing on top of fetch. Each `data: {...}` line becomes
 * an event delivered through the `onDelta` / `onDone` / `onError` callbacks.
 */

export interface AiMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * Structured error kind biar UI bisa render contextual response
 *   - 'unauthorized' → user belum login / session expired → tampilin login CTA
 *   - 'server'       → 5xx atau response gak parseable → "AI sedang gangguan"
 *   - 'network'      → fetch reject (offline, DNS, dll)
 *   - 'aborted'      → user click Stop (gak treated as error sebenernya)
 *   - 'unknown'      → fallback
 *
 * Sengaja TIDAK expose raw response body / status code ke UI — kalau perlu
 * debug (status, detail), lewat console.warn dari layer ini, BUKAN pesan
 * yang muncul ke chat bubble. Worst-case: engineer-user dump cookie/JSON
 * mentah ke screenshot yang di-share ke publik.
 */
export type StreamErrorKind = 'unauthorized' | 'server' | 'network' | 'aborted' | 'unknown';

export interface StreamErrorInfo {
  kind: StreamErrorKind;
  message: string;
}

export interface StreamHandlers {
  onDelta: (text: string) => void;
  onDone?: (reason?: string) => void;
  onError?: (err: StreamErrorInfo) => void;
}

export interface StreamOptions {
  messages: AiMessage[];
  system?: string;
  model?: string;
  signal?: AbortSignal;
}

const API_BASE = (import.meta.env.VITE_API_PUBLIC_URL as string | undefined) || '';
const STREAM_URL = `${API_BASE}/api/playground/ai/stream`;

export async function streamAi(
  options: StreamOptions,
  handlers: StreamHandlers
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(STREAM_URL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        messages: options.messages,
        system: options.system,
        model: options.model,
      }),
      signal: options.signal,
    });
  } catch (e) {
    // Network-level failure (offline, DNS, CORS pre-flight, dll)
    console.warn('[aiStream] network error:', (e as Error).message);
    handlers.onError?.({
      kind: 'network',
      message: 'Koneksi ke AI server gagal. Cek koneksi internet kamu.',
    });
    return;
  }

  if (!res.ok || !res.body) {
    // Log raw detail ke console (untuk debug developer), jangan tampilin
    // ke chat bubble — engineer-user yang screenshot bisa accidentally
    // share JWT/cookie/internal struct.
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      /* ignore */
    }
    console.warn(`[aiStream] HTTP ${res.status}`, detail.slice(0, 500));

    if (res.status === 401) {
      handlers.onError?.({
        kind: 'unauthorized',
        message: 'Login dulu buat pakai AI assistant.',
      });
    } else if (res.status >= 500) {
      handlers.onError?.({
        kind: 'server',
        message: 'AI server lagi gangguan. Coba lagi sebentar ya.',
      });
    } else {
      handlers.onError?.({
        kind: 'unknown',
        message: 'Gagal kirim pertanyaan ke AI. Coba lagi.',
      });
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let nlIdx;
      while ((nlIdx = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, nlIdx).replace(/\r$/, '');
        buffer = buffer.slice(nlIdx + 1);
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload) continue;
        if (payload === '[DONE]') {
          handlers.onDone?.();
          return;
        }
        try {
          const json = JSON.parse(payload);
          if (json.error) {
            // Backend mid-stream error (rate limit, model failure, dll).
            // Log raw, kasih user message generic.
            console.warn('[aiStream] backend stream error:', json.error);
            handlers.onError?.({
              kind: 'server',
              message: 'AI berhenti di tengah jalan. Coba kirim ulang.',
            });
            return;
          }
          if (typeof json.delta === 'string') {
            handlers.onDelta(json.delta);
          }
          if (json.done) {
            handlers.onDone?.(json.reason);
            return;
          }
        } catch {
          // ignore malformed
        }
      }
    }
    handlers.onDone?.();
  } catch (e) {
    if ((e as Error).name === 'AbortError') {
      handlers.onDone?.('aborted');
    } else {
      console.warn('[aiStream] read error:', (e as Error).message);
      handlers.onError?.({
        kind: 'network',
        message: 'Koneksi terputus saat streaming. Coba lagi.',
      });
    }
  }
}
